import { router } from 'umi'
import { message } from 'antd'
import { downloadLink } from '@/utils/utils'
import { extend, RequestOptionsInit } from 'umi-request'
import { stringify } from 'querystring'
import json from './json'

export interface RequestOptions extends RequestOptionsInit {
  isDownload?: boolean
  hideErrorMessage?: boolean
  autoHump?: boolean
}

export interface Pagination {
  current_page: number
  page_size: number
}

export interface ResponseData<T> {
  code: number
  message: string
  data: T
}

export interface ResponseData<T> {
  code: number
  message: string
  data: T
}

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（未登录或登录超时）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
}

// 配置request请求时的默认参数
const request = extend({
  credentials: 'include', // 默认请求是否带上 cookie
  responseType: 'json', // 默认返回的数据类型为 json
})

function handleError(errorMessage: string): void {
  message.error(errorMessage)
}

function handleUnauthorized(): void {
  const { href, origin, search } = window.location
  if (search.includes('redirect')) {
    return
  }
  let redirect = href.replace(origin, '')
  router.replace({
    pathname: '/user/login',
    search: stringify({ redirect }),
  })
}

// response status code exception
request.interceptors.response.use(response => {
  if (response.status !== 200 && response.status !== 304) {
    message.destroy()
    if (response.status === 401) {
      handleUnauthorized()
    }
    handleError(codeMessage[response.status])
    throw new Error()
  }
  return response
})

const mRequest = async function(url: string, options?: RequestOptions): Promise<ResponseData<any>> {
  try {
    options = {
      method: 'get',
      responseType: 'json',
      ...options,
    }

    // 下载链接

    if (options.isDownload) {
      downloadLink(url, options.data || options.params)
      return null
    }

    // 自动转换属性名格式开关打开
    if (options.autoHump) {
      if ((options.method || '').toUpperCase() === 'GET') {
        options.params = json.underline(options.params)
      } else if ((options.method || '').toUpperCase() === 'POST') {
        options.data = json.underline(options.data)
      }
    }
    const res = await request(url, options)

    const { code, message } = res || {}
    if (options.responseType === 'json') {
      if (code) {
        if (code === 5) {
          handleUnauthorized()
        }
        if (!options.hideErrorMessage) {
          handleError(message)
          throw new Error(message)
        }
      }
      if (res.code === 0 && options.autoHump) {
        res.data = json.hump(res.data)
      }
    }
    return res
  } catch (error) {
    console.log(error, 'error')
    throw new Error(error.message)
  }
}

export const post = (url: string, data: any) =>
  mRequest(url, {
    method: 'post',
    data,
  }).then(res => res.data || res)

export const get = (url: string) => mRequest(url).then(res => res.data || res)

export const exportXLSX = (url: string, data: any) =>
  mRequest(url, { method: 'post', responseType: 'blob', data }).then(blob => {
    const u = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = u
    a.click()
    window.URL.revokeObjectURL(u)
  })

export default mRequest
